import { generateClassName } from '@klv/shared/utils/functions';
import BaseGrid from '@klv/utils/grid';
import Buttons from '@klv/molecules/buttons';
import type { TFiveToTwelve, ILogosProps, ILogo } from './types';

import * as styles from './styles/logos.module.scss';
import { Wrapper } from '@klv/utils/wrapper';
import Image from '@klv/atoms/image';
import { getBackgroundType } from '@klv/utils/wrapper/functions';

const ScrollingLogos = ({ logos }: { logos: ILogo[]; }) => {
  const minimumLogos = 8;
  const animationDuration = logos.length * 100 / minimumLogos;

  return (
    <BaseGrid className={styles.logoContainer}>
      <div className={styles.appContainer}>
        <div className={styles.scrollableLogos}>
          {[...Array(2)].map((_, idx) => (
            <div className={styles.animatedLogos} key={`wrapper-${idx}`} style={{ animationDuration: `${animationDuration}s` }}>
              {logos.concat(logos).map((logo, index) => (
                <Image
                  className={styles.scrollableImage}
                  key={`logo-${index}-${idx}`}
                  imageData={logo.image.gatsbyImage}
                  altText={logo.image?.altText}
                  loading="eager"
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </BaseGrid>
  );
};

const Logos = ({
  background = 'dark',
  backgroundColor,
  customPadding = { customPaddingBottom: 'md', customPaddingTop: 'md' },
  eyebrow,
  buttons = [],
  buttonVariants = ['ghost'],
  subhead = '',
  headline = '',
  useAnimatedScroll = false,
  logos,
  ...props
}: ILogosProps) => {
  const logoCount = logos.length as TFiveToTwelve;
  const desktopTopRowCount = [6, 11, 12].includes(logoCount) ? 6 : 5;

  const backgroundColorName = backgroundColor !== 'lightSand' ? backgroundColor : background;
  const buttonColor =  getBackgroundType(backgroundColorName) === 'light' ? 'dark' : 'light';
    
  return (
    <Wrapper customPadding={customPadding} backgroundColor={backgroundColorName}>
      <BaseGrid className={generateClassName([styles.textGrid, headline || subhead ? styles.hasChildren : ''])}>
        {eyebrow && <p className={generateClassName(['eyebrow', styles.styledEyebrow])}>{eyebrow}</p>}
        {headline && (
          <h2 className={generateClassName(['h2', styles.styledHeadline, subhead ? styles.hasSubhead : ''])}
            dangerouslySetInnerHTML={{ __html: headline }}
          />
        )}
        {subhead && (
          <p className={generateClassName(['p2', styles.subHead])}
          >
            {subhead}
          </p>
        )}
      </BaseGrid>

      {useAnimatedScroll ? (
        <ScrollingLogos logos={logos}/>
      ) : (
        <div className={generateClassName([styles.logoContainer, desktopTopRowCount === 6 ? styles.topRowSix : ''])}>
          {logos.map((logo) => {
            if (!logo?.image?.gatsbyImage) {
              return null;
            }
        
            const image = (
              <Image
                className={styles.styledImage}
                altText={logo.image.altText}
                imageData={logo.image.gatsbyImage}
                loading='eager'
                {...props}
              />
            );
        
            return (
              <div className={styles.styledLogo}
                key={JSON.stringify(logo.image)}
              >
                {logo?.url ? (
                  <a className={styles.logoLink} href={logo.url}>{image}</a>
                ) : (
                  image
                )}
              </div>
            );
          })}
      </div>
      )}

      {buttons !== undefined && (
        <BaseGrid>
          <Buttons
            className={generateClassName([styles.buttonsWrapper])}
            buttons={buttons}
            variants={buttonVariants}
            buttonColor={buttonColor}
          />
        </BaseGrid>
      )}
    </Wrapper>
  );
};

export default Logos;
